'use client';
import { useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useCounterAnimation } from '@/hooks/useCounterAnimation';

function AnimatedStat({ number, label }: { number: string; label: string }) {
  const match = number.match(/^([^\d]*)(\d+(?:\.\d+)?)([^\d]*)$/);
  const numericValue = match ? parseFloat(match[2]) : 0;
  const prefix = match ? match[1] : '';
  const suffix = match ? match[3] : '';

  const { displayValue, elementRef } = useCounterAnimation({
    end: numericValue,
    duration: 2000,
    prefix,
    suffix,
  });

  return (
    <div ref={elementRef} className="reveal text-center py-8 border-t border-border">
      <div className="text-5xl md:text-6xl font-bold tracking-tight mb-2 text-primary neon-glow">
        {displayValue}
      </div>
      <p className="text-[10px] uppercase tracking-ultra-wide text-muted-foreground">
        {label}
      </p>
    </div>
  );
}

export default function AboutPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const stats = [
    { id: 'stat_1', number: '20+', label: 'Years Building Businesses' },
    { id: 'stat_2', number: '2000', label: 'Best Odds Corp Founded' },
    { id: 'stat_3', number: '135658+', label: 'Afterhour Followers' },
    { id: 'stat_4', number: '17186+', label: 'X (Twitter) Followers' }
  ];

  const values = [
    {
      id: 'value_1',
      icon: 'BoltIcon',
      title: 'Execution',
      description: 'We ship weekly. Not decks, not strategy docs — real work that moves metrics.'
    },
    {
      id: 'value_2',
      icon: 'UserGroupIcon',
      title: 'Operator Mindset',
      description: 'We think like founders. We identify the constraint, build the plan, and execute — not advise from the sidelines.'
    },
    {
      id: 'value_3',
      icon: 'LightBulbIcon',
      title: 'AI Leverage',
      description: 'We use AI to amplify output, tighten systems, and move faster — without sacrificing quality or brand voice.'
    },
    {
      id: 'value_4',
      icon: 'ShieldCheckIcon',
      title: 'Accountability',
      description: 'US-based operation, US business hours, transparent capacity planning. You always know what we\'re working on.'
    }
  ];

  const processSteps = [
    { id: 'p1', step: '01', title: 'Diagnostic Audit', desc: 'Map the business — offer, positioning, marketing, operations, and follow-up.' },
    { id: 'p2', step: '02', title: 'Bottleneck Analysis', desc: 'Identify where revenue is leaking and where the highest-leverage opportunities are.' },
    { id: 'p3', step: '03', title: 'Prioritization', desc: 'Rank opportunities by impact and effort. First sprint addresses the highest-value constraint.' },
    { id: 'p4', step: '04', title: 'Roadmap Creation', desc: 'Clear, phased execution plan. You know what gets built, in what order, and why.' },
    { id: 'p5', step: '05', title: 'Weekly Execution', desc: 'Sprints planned Monday, executed through the week, reviewed Friday. Real work ships every week.' },
    { id: 'p6', step: '06', title: 'Reporting & Visibility', desc: 'Transparent capacity planning and progress reporting. You always know what\'s happening.' },
    { id: 'p7', step: '07', title: 'Refinement', desc: 'Iterate based on data. What\'s working gets doubled down. What isn\'t gets adjusted.' }
  ];

  const differentiators = [
    {
      id: 'd1',
      icon: 'UserIcon',
      title: 'Founder-Led, Not Agency-Managed',
      description: 'Daniel leads every engagement directly. No account manager layers, no junior team handoffs. You work with the operator making the decisions.'
    },
    {
      id: 'd2',
      icon: 'CpuChipIcon',
      title: 'AI-Native Execution',
      description: 'We use AI across every workflow — content, code, research, automation. Daniel built the current Best Odds website himself using AI as leverage, and personally built the X Cleanup Console Chrome extension. More private tools are available — some free, some through subscription. That\'s the capability you\'re hiring.'
    },
    {
      id: 'd3',
      icon: 'MagnifyingGlassIcon',
      title: 'Bottleneck-First Thinking',
      description: 'We don\'t start with a service menu. We start by finding the constraint — the revenue leak, the positioning gap, the follow-up failure — and build the plan from there.'
    },
    {
      id: 'd4',
      icon: 'BoltIcon',
      title: 'Sprint-Based, Not Retainer-Bloated',
      description: 'Work is organized in focused sprints with clear deliverables. No bloated retainers, no mystery work, no scope creep without discussion.'
    }
  ];

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        {/* Hero Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal">
              <SectionHeader
                eyebrow="About Best Odds Corp"
                title="An AI-Powered Execution Partner Built by an Operator"
                description="Best Odds Corp was founded over 20 years ago by Daniel Moravec — a formally trained marketer, active trader, and AI-fluent builder who has spent two decades building, testing, and repositioning businesses across multiple industries. This is not a generic agency. It's an operator-led execution partner for founders who need results, not more strategy decks."
                align="center"
                className="mb-16"
              />
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div className="reveal">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">The Origin</span>
                <h2 className="text-4xl font-bold mb-6">Founded in College. Evolved Over 20 Years.</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-6">
                  Daniel Moravec founded Best Odds Corp in college, building digital media properties and performance marketing systems from scratch. Formally trained in Marketing and Business Communication at The College of St. Scholastica, he combined academic grounding with hands-on execution — building, testing, and iterating across multiple business models over two decades.
                </p>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  The company has evolved significantly since its founding — from niche digital media and affiliate systems to a focused AI-powered execution partner for founders and operators. The core hasn't changed: identify the constraint, build the plan, and ship the work.
                </p>
              </div>
              <div className="reveal">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">The Present</span>
                <h2 className="text-4xl font-bold mb-6">Sprint-Based. AI-Driven. Operator-Led.</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-6">
                  Today, Best Odds Corp operates as a sprint-based execution partner — building apps, Chrome extensions, automations, and growth systems using AI as leverage. Daniel is an active trader and content creator in the finance and trading space, with 135,658+ followers on Afterhour and 17,186+ on X — bringing real decision-making discipline and pattern recognition to every client engagement.
                </p>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  He built the current Best Odds website himself using AI tools — and personally built the X Cleanup Console Chrome extension, with more private tools available through subscription. The model is simple: find the bottleneck, build the plan, and ship execution every week.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="By The Numbers"
                title="Real Background. Real Credibility."
                align="center"
              />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <AnimatedStat key={stat.id} number={stat.number} label={stat.label} />
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="What Drives Us"
                title="How We Operate"
                align="center"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value) => (
                <div key={value.id} className="reveal p-8 border border-border hover:border-primary transition-colors">
                  <div className="w-12 h-12 border border-border flex items-center justify-center mb-6">
                    <Icon name={value.icon as any} size={24} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                  <p className="text-body-sm text-muted-foreground">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What Makes Us Different */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="Why Best Odds Corp"
                title="Not a Generic Agency"
                description="Four things that make this engagement model different from a traditional agency or freelancer relationship."
                align="center"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {differentiators.map((d) => (
                <div key={d.id} className="reveal p-8 border border-border hover:border-primary transition-colors bg-card">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 border border-primary flex items-center justify-center flex-shrink-0">
                      <Icon name={d.icon as any} size={24} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-3">{d.title}</h3>
                      <p className="text-body-sm text-muted-foreground">{d.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="The Process"
                title="Systematic. Founder-Friendly. Execution-Focused."
                description="Every engagement follows a 7-step process designed to find the constraint, build the plan, and ship execution every week."
                align="center"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {processSteps.slice(0, 4).map((step) => (
                <div key={step.id} className="reveal p-8 border border-border hover:border-primary transition-colors">
                  <div className="text-4xl font-bold text-primary mb-4 opacity-60">{step.step}</div>
                  <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                  <p className="text-body-sm text-muted-foreground">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {processSteps.slice(4).map((step) => (
                <div key={step.id} className="reveal p-8 border border-border hover:border-primary transition-colors">
                  <div className="text-4xl font-bold text-primary mb-4 opacity-60">{step.step}</div>
                  <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                  <p className="text-body-sm text-muted-foreground">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6">
          <div className="max-w-[1600px] mx-auto text-center">
            <div className="reveal">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Start with a Sprint. Build the roadmap first.
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
                Every engagement begins with a paid diagnostic sprint — we identify your bottlenecks, map the highest-value opportunities, and deliver a prioritized roadmap. Then you choose your path.
              </p>
              <p className="text-body-sm text-muted-foreground max-w-xl mx-auto mb-8">
                No pressure to commit to a monthly package before we understand your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href="/book"
                  className="inline-block px-8 py-4 bg-primary text-primary-foreground text-xs uppercase tracking-widest hover:bg-primary/90 transition-colors font-semibold border-glow"
                >
                  Book a Sprint
                </Link>
                <Link
                  href="/pricing"
                  className="inline-block px-8 py-4 border border-border text-foreground text-xs uppercase tracking-widest hover:border-primary hover:text-primary transition-colors font-semibold"
                >
                  See What a Sprint Uncovers
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}